// Settings → Master Menu: the "Hide inactive items" filter. Display only — a hidden
// product is still in the list the page fetched, and still in every reorder it sends.
// Pure functions, apart from the two that read and write the browser's stored choice.

export const HIDE_INACTIVE_STORAGE_KEY = "freshorder:master-menu:hide-inactive";

type MenuProduct = { id: number; isActive: boolean; sizeOfProductId?: number | null };

/** The choice this browser stored; on when nothing is stored or storage is unavailable. */
export function readHideInactive(): boolean {
  try {
    return localStorage.getItem(HIDE_INACTIVE_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeHideInactive(hide: boolean): void {
  try {
    localStorage.setItem(HIDE_INACTIVE_STORAGE_KEY, hide ? "1" : "0");
  } catch {
    // Storage unavailable (private mode, quota): the choice lasts until the page reloads.
  }
}

/**
 * The products the filter takes off the Master Menu: every switched-off one, except
 *  - a lead (shared/productSizes.ts) that still has an active size — its row is where
 *    those sizes are managed, so it stays, wearing its Inactive badge;
 *  - one the caller pins (a row holding a price that is typed but not saved).
 */
export function hiddenInactiveIds<P extends MenuProduct>(products: P[], pinned?: (product: P) => boolean): Set<number> {
  const leadsOfActiveSizes = new Set<number>();
  for (const product of products) {
    if (product.isActive && product.sizeOfProductId != null) leadsOfActiveSizes.add(product.sizeOfProductId);
  }
  const hidden = new Set<number>();
  for (const product of products) {
    if (product.isActive || leadsOfActiveSizes.has(product.id) || pinned?.(product)) continue;
    hidden.add(product.id);
  }
  return hidden;
}

const NOTHING_HIDDEN: ReadonlySet<number> = new Set();

/**
 * A category's product ids after one drawn row is dropped on another: the dragged row
 * takes the target's place. Null when either is not a drawn row of the category.
 *
 * `categoryIds` is the WHOLE category in menu order, hidden rows included. The server
 * numbers sort_order by position in the list it is sent and leaves every other product
 * alone, so a list of only the drawn rows would renumber them from 0 and scatter the
 * hidden ones among them.
 *
 * A hidden row travels with the drawn row above it. A size is created directly under
 * its lead, so a hidden size follows its lead: left behind, it would sit above the lead
 * unseen, and the customer menu places a size group where its first member sits
 * (groupProductSizes) — the card would jump back there the day the size is switched on.
 * With nothing hidden this is a plain move, as before the filter existed.
 */
export function reorderCategoryIds(
  categoryIds: number[],
  activeId: number,
  overId: number,
  hidden: ReadonlySet<number> = NOTHING_HIDDEN,
): number[] | null {
  // One block per drawn row: the row, then the hidden rows under it. Hidden rows above
  // the first drawn row form a block of their own, which no drag can pick up.
  const blocks: number[][] = [];
  for (const id of categoryIds) {
    if (hidden.has(id) && blocks.length > 0) blocks[blocks.length - 1].push(id);
    else blocks.push([id]);
  }
  const from = blocks.findIndex((block) => block[0] === activeId);
  const to = blocks.findIndex((block) => block[0] === overId);
  if (from === -1 || to === -1) return null;
  const [moved] = blocks.splice(from, 1);
  blocks.splice(to, 0, moved);
  return ([] as number[]).concat(...blocks);
}
